import { createFileRoute, useBlocker } from '@tanstack/react-router';
import { useEffect, useId, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarOff, Lock, Users } from 'lucide-react';
import clsx from 'clsx';
import { apiRequest, ApiError } from '../lib/api';
import { useToast } from '../lib/toast';
import { useConfirm } from '../components/ui/Confirm';
import { EmptyState } from '../components/ui/EmptyState';
import { ErrorState } from '../components/ui/ErrorState';
import { Skeleton } from '../components/ui/Skeleton';
import {
  AttendanceRow,
  deriveTally,
  type RosterRow,
} from '../components/academics/AttendanceRosterList';
import { BatchPicker } from '../components/academics/BatchPicker';
import { AttendanceDatePicker } from '../components/academics/AttendanceDatePicker';
import {
  BulkButtons,
  MobileTallyChip,
  SessionSummaryRail,
  SubmitButton,
} from '../components/academics/SessionSummary';
import type {
  AttendanceDay,
  AttendanceStatus,
  BatchRecord,
} from '../components/academics/types';

/** Why the requesting user can't edit this date (server-computed). */
type LockReason = 'past' | 'future' | null;

/** Max records the bulk endpoint accepts per call (backend contract). */
const BULK_CHUNK_SIZE = 60;

const todayIso = (): string => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
};

const formatDate = (iso: string): string => {
  // iso is YYYY-MM-DD; parse as local to avoid TZ drift.
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return iso;
  const date = new Date(y, m - 1, d);
  return date.toLocaleDateString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

interface RosterResponse {
  day: { id: string | null; batchId: string; date: string; finalizedAt: string | null };
  /** Whether the requesting user may edit attendance for this date. */
  editable: boolean;
  /** Why editing is locked, for UI messaging. `null` when editable. */
  lockReason: LockReason;
  items: ReadonlyArray<{
    studentId: string;
    name: string;
    admissionNumber: string;
    status: AttendanceStatus | null;
    notes: string | null;
    markedAt: string | null;
    markedBy: string | null;
  }>;
}

/**
 * Compact signature of the working roster. Status and the TRIMMED note are both
 * client-mutable, so both feed the hash — a note edit must flip isDirty and the
 * post-submit reseed baseline must match the trimmed value the server persists.
 */
const rosterSignature = (rows: ReadonlyArray<RosterRow>): string =>
  rows.map((r) => `${r.studentId}:${r.status}:${r.notes.trim()}`).join('|');

export const Route = createFileRoute('/_authed/academics/attendance')({
  component: AttendancePage,
});

function AttendancePage() {
  const { toast } = useToast();
  const { confirm } = useConfirm();
  const queryClient = useQueryClient();
  const batchLabelId = useId();

  const today = todayIso();
  const [batchId, setBatchId] = useState<string>('');
  const [date, setDate] = useState<string>(today);
  const [rows, setRows] = useState<RosterRow[]>([]);
  // Baseline signature captured at seed time — the dirty check compares the
  // working rows against this. Re-seeding (on batch/date change or after a
  // successful submit) resets the baseline so Submit greys out again.
  const [baselineSig, setBaselineSig] = useState<string>('');
  // Seed-guard so background refetches never clobber in-progress edits. We
  // re-seed only when the (batch, date) pair changes or after a save invalidate.
  const [seededKey, setSeededKey] = useState<string>('');

  const batchesQuery = useQuery({
    queryKey: ['batches', 'attendance-pool'] as const,
    queryFn: ({ signal }) =>
      apiRequest<{ items: BatchRecord[] }>('/api/batches', {
        signal,
        query: { page: 1, pageSize: 100 },
      }),
    staleTime: 60_000,
  });

  const activeBatches = useMemo(
    () => (batchesQuery.data?.items ?? []).filter((b) => b.status === 'active'),
    [batchesQuery.data],
  );

  // No auto-select: the teacher must choose a batch before any roster fetch.
  const rosterEnabled = Boolean(batchId && date);
  const rosterKey = `${batchId}::${date}`;

  const rosterQuery = useQuery({
    queryKey: ['attendance', 'roster', batchId, date] as const,
    enabled: rosterEnabled,
    // Avoid background refetches clobbering edits mid-session.
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    queryFn: ({ signal }) =>
      apiRequest<RosterResponse>('/api/attendance/roster', {
        signal,
        query: { batchId, date },
      }),
  });

  const serverDay = rosterQuery.data?.day ?? null;
  const isFinalized = Boolean(serverDay?.finalizedAt);
  // Server-driven edit permission for the requesting user + date. Default to
  // editable while loading so the UI doesn't flash a lock; the roster only
  // renders once data has arrived.
  const editable = rosterQuery.data?.editable ?? true;
  const lockReason: LockReason = rosterQuery.data?.lockReason ?? null;

  // Seed local editable rows from the server roster. A null status means no
  // record exists for that student yet, which seeds as the client-only
  // 'unmarked' state — never present by default (the honest register). A
  // finalized day returns real statuses for everyone enrolled as of the date,
  // so the `?? 'unmarked'` only ever fires for a genuinely unrecorded student
  // (rare now that the roster is enrolment-scoped). Re-seeds when batch/date
  // changes or after a successful save invalidates and the query refetches. The
  // baseline signature is captured here so the dirty check has a clean reference.
  useEffect(() => {
    if (!rosterQuery.data) return;
    if (seededKey === rosterKey) return;
    const seeded: RosterRow[] = rosterQuery.data.items.map((it) => ({
      studentId: it.studentId,
      name: it.name,
      admissionNumber: it.admissionNumber,
      status: it.status ?? 'unmarked',
      notes: it.notes ?? '',
      markedAt: it.markedAt,
    }));
    setRows(seeded);
    setBaselineSig(rosterSignature(seeded));
    setSeededKey(rosterKey);
  }, [rosterQuery.data, rosterKey, seededKey]);

  const tally = useMemo(() => deriveTally(rows), [rows]);

  // Dirty when the working roster diverges from the last seeded/submitted
  // state. Submit is active when dirty OR the day was never submitted, but only
  // once EVERY student is marked — an unmarked student (the honest-register
  // default) blocks submission, since the register would otherwise record a
  // status no one chose.
  const isDirty = rosterSignature(rows) !== baselineSig;
  const canSubmit = (isDirty || !isFinalized) && tally.unmarked === 0;
  const isSubmitted = isFinalized && !isDirty;

  // Guard date/batch changes: switching the (batch, date) selection re-seeds
  // the roster and would silently drop unsaved edits. When dirty, ask first;
  // only proceed (and let the seed effect re-seed) on confirm. All date paths
  // — steppers, native picker, reset-to-today — funnel through the picker's
  // onChange, so this single wrapper covers them.
  const confirmDiscard = async (): Promise<boolean> => {
    if (!isDirty) return true;
    return confirm({
      title: 'Discard unsaved changes?',
      description: 'You have unsaved attendance changes. Discard them and continue?',
      confirmLabel: 'Discard',
    });
  };

  // Block in-app navigation, refresh, and tab-close while the register has
  // unsaved edits. confirmDiscard() already returns true when not dirty (so we
  // never block a clean roster) and true when the user confirms discard, so we
  // block precisely when they decline. enableBeforeUnload drives the browser's
  // native refresh/close prompt.
  useBlocker({
    shouldBlockFn: async () => !(await confirmDiscard()),
    enableBeforeUnload: isDirty,
  });

  const handleDateChange = async (next: string) => {
    if (next === date) return;
    if (await confirmDiscard()) setDate(next);
  };

  const handleBatchChange = async (next: string) => {
    if (next === batchId) return;
    if (await confirmDiscard()) setBatchId(next);
  };

  const setStatus = (studentId: string, status: AttendanceStatus) => {
    setRows((prev) =>
      prev.map((r) =>
        r.studentId === studentId
          ? // Returning a student to present discards any exception note — an
            // explicit edit the round-trip then persists as cleared. Other
            // statuses keep the note so toggling late↔absent never loses it.
            { ...r, status, notes: status === 'present' ? '' : r.notes }
          : r,
      ),
    );
  };

  const setNote = (studentId: string, notes: string) => {
    setRows((prev) =>
      prev.map((r) => (r.studentId === studentId ? { ...r, notes } : r)),
    );
  };

  const setAll = (status: AttendanceStatus) => {
    setRows((prev) => prev.map((r) => ({ ...r, status })));
  };

  const refreshRoster = async () => {
    // Order matters. Refetch FIRST so the cache holds the persisted values,
    // THEN drop the seed-guard so the re-seed reads fresh data. Resetting the
    // guard before the refetch re-seeds from the STALE pre-submit cache (where
    // a not-yet-saved student is status=null -> 'unmarked'), and the guard then
    // blocks the post-refetch re-seed — so a just-submitted "absent" would show
    // as "unmarked" until the date key changes. Awaiting the invalidate ensures
    // the refetch has settled before we re-seed.
    await queryClient.invalidateQueries({
      queryKey: ['attendance', 'roster', batchId, date],
    });
    setSeededKey('');
  };

  /**
   * Submit = save records and finalize, atomically. We:
   *   1. upsert the day header to obtain a dayId,
   *   2. POST every row's status, chunked to the bulk limit, sequentially,
   *      attaching `finalize: true` to the LAST chunk only — so the records-save
   *      and the day-finalize commit together in one batch with no "records
   *      saved but day unfinalized" window, and so finalize never lands before
   *      the final records in the rare >BULK_CHUNK_SIZE multi-chunk case.
   *
   * No standalone PATCH finalize is needed: finalize rides the records POST. The
   * flag is attached only when the day was not already finalized — re-sending
   * finalize on an already-finalized day is a server-side no-op, but we skip it
   * to keep the payload honest.
   *
   * `wasFinalized` is captured by the caller at click time (before the refetch
   * flips it) and passed in so this branch is deterministic.
   */
  const submitMutation = useMutation({
    mutationFn: async (wasFinalized: boolean) => {
      const dayRes = await apiRequest<{ item: AttendanceDay }>('/api/attendance/days', {
        method: 'POST',
        body: { batchId, date },
      });
      const id = dayRes.item.id;

      // Only the four real statuses are ever persisted; the submit gate already
      // guarantees no row is 'unmarked' here, but flatMap-narrow defends the
      // wire contract and keeps `status` typed as AttendanceStatus.
      const records = rows.flatMap((r) =>
        r.status === 'unmarked'
          ? []
          : [
              {
                studentId: r.studentId,
                status: r.status,
                ...(r.notes.trim() ? { notes: r.notes.trim() } : {}),
              },
            ],
      );

      for (let i = 0; i < records.length; i += BULK_CHUNK_SIZE) {
        const chunk = records.slice(i, i + BULK_CHUNK_SIZE);
        const isLastChunk = i + BULK_CHUNK_SIZE >= records.length;
        await apiRequest<{ ok: true; count: number; finalizedAt: string | null }>(
          `/api/attendance/days/${id}/records`,
          {
            method: 'POST',
            body: {
              records: chunk,
              ...(isLastChunk && !wasFinalized ? { finalize: true } : {}),
            },
          },
        );
      }
    },
    onSuccess: async () => {
      await refreshRoster();
      toast('Attendance submitted.', { variant: 'success' });
    },
    onError: (err: unknown) => {
      toast(err instanceof ApiError ? err.message : 'Could not submit attendance.', {
        variant: 'error',
      });
    },
  });

  const onSubmit = async () => {
    // Guard: every student must be marked before the register can be recorded.
    // canSubmit already disables the button, but a keyboard/programmatic submit
    // could still reach here, so we hard-stop and tell the user what's left.
    if (tally.unmarked > 0) {
      toast(
        `${tally.unmarked} student${tally.unmarked === 1 ? '' : 's'} not yet marked — mark everyone before submitting.`,
        { variant: 'error' },
      );
      return;
    }
    const parts = [
      `${tally.present} present`,
      `${tally.late} late`,
      `${tally.absent} absent`,
      ...(tally.excused ? [`${tally.excused} excused`] : []),
    ];
    const ok = await confirm({
      title: 'Submit attendance?',
      description: `Submit attendance for ${selectedBatch?.name ?? 'this batch'}, ${formatDate(date)} — ${parts.join(' · ')}?`,
      confirmLabel: 'Submit',
      // Require a per-session attestation tick only when finalizing for the first
      // time. A same-day re-submit of an already-finalized day was already
      // attested, so we don't re-prompt.
      acknowledge: isFinalized
        ? undefined
        : {
            label: `I have reviewed all ${tally.total} students and marked each one.`,
          },
    });
    // Snapshot the finalized state at click time so the skip-finalize decision
    // isn't affected by the post-submit refetch.
    if (ok) submitMutation.mutate(isFinalized);
  };

  const selectedBatch = activeBatches.find((b) => b.id === batchId) ?? null;
  const isBusy = submitMutation.isPending;
  const hasRoster = rows.length > 0;

  return (
    <div className="px-4 pb-32 pt-6 sm:px-6 sm:pt-8 min-[1120px]:pb-12">
      <div className="mx-auto flex w-full max-w-[1320px] items-start justify-center gap-8">
        {/* Centered marking column (~940px). */}
        <div className="w-full min-w-0 max-w-[940px]">
          <header className="mb-5">
            <p className="mb-3 inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-[color:var(--color-text-subtle)]">
              <span aria-hidden="true" className="h-px w-[18px] bg-gold-500" />
              Babul Ilm · Attendance
            </p>
            <h1 className="text-brand-gradient font-display text-[27px] font-bold leading-tight tracking-[0.01em] sm:text-[34px]">
              Mark today&rsquo;s session
            </h1>
            <p className="mt-2.5 max-w-[54ch] text-sm text-[color:var(--color-text-muted)] sm:text-[14.5px]">
              Tap <b className="font-semibold text-[color:var(--color-text)]">All present</b> (or
              All absent) to start, then flag the exceptions. Submit records the register for this
              date.
            </p>
          </header>

          {/* Combined session header card: batch picker + date stepper. */}
          <div className="mb-5 grid grid-cols-1 overflow-visible rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] shadow-[var(--shadow-md)] sm:grid-cols-2">
            <div className="flex flex-col gap-2.5 p-[18px] sm:px-5">
              <span
                id={batchLabelId}
                className="text-[10.5px] font-semibold uppercase tracking-[0.18em] text-[color:var(--color-text-subtle)]"
              >
                Batch
              </span>
              <BatchPicker
                batches={activeBatches}
                value={batchId}
                onChange={handleBatchChange}
                labelId={batchLabelId}
                loading={batchesQuery.isLoading}
                disabled={isBusy}
              />
            </div>
            <div className="flex flex-col gap-2.5 border-t border-[color:var(--color-border)] p-[18px] sm:border-l sm:border-t-0 sm:px-5">
              <span className="text-[10.5px] font-semibold uppercase tracking-[0.18em] text-[color:var(--color-text-subtle)]">
                Session date
              </span>
              <AttendanceDatePicker
                value={date}
                onChange={handleDateChange}
                todayIso={today}
                disabled={isBusy}
              />
            </div>
          </div>

          {!batchId ? (
            <div className="rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] shadow-[var(--shadow-md)]">
              <EmptyState
                icon={<Users size={32} aria-hidden="true" />}
                title={batchesQuery.isLoading ? 'Loading batches…' : 'Select a batch to begin'}
                description="Choose a batch above to load its roster for the chosen date."
              />
            </div>
          ) : rosterQuery.isError ? (
            <ErrorState
              title="Could not load the roster"
              description={
                rosterQuery.error instanceof ApiError ? rosterQuery.error.message : undefined
              }
              onRetry={() => rosterQuery.refetch()}
            />
          ) : rosterQuery.isLoading ? (
            <RosterSkeleton />
          ) : !hasRoster ? (
            <div className="rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] shadow-[var(--shadow-md)]">
              <EmptyState
                icon={<Users size={32} aria-hidden="true" />}
                title="No enrolled students"
                description="This batch has no active enrollments for this date, so there is nothing to mark."
              />
            </div>
          ) : (
            <>
              {/* Inline tally chip (mobile / narrow). */}
              <div className="mb-4">
                <MobileTallyChip tally={tally} />
              </div>

              {!editable ? <LockBanner reason={lockReason} /> : null}

              {/* Unified roster card. */}
              <div className="overflow-hidden rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] shadow-[var(--shadow-md)]">
                <div className="flex flex-col gap-2.5 border-b border-[color:var(--color-border)] px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-5">
                  <div className="min-w-0">
                    <p className="font-display text-base font-bold tracking-[0.02em]">Roster</p>
                    <p className="mt-0.5 text-[12.5px] text-[color:var(--color-text-muted)]">
                      {formatDate(date)} · {tally.total}{' '}
                      {tally.total === 1 ? 'student' : 'students'}
                      {editable ? ' · tap present / absent, clock for late' : ''}
                    </p>
                  </div>
                  <Legend showUnmarked={tally.unmarked > 0} />
                </div>

                <ul>
                  {rows.map((row) => (
                    <AttendanceRow
                      key={row.studentId}
                      row={row}
                      readOnly={!editable}
                      onSetStatus={setStatus}
                      onSetNote={setNote}
                    />
                  ))}
                </ul>
              </div>
            </>
          )}
        </div>

        {/* Smart wide-screen rail (>=1120px). Before a roster exists (no batch,
            empty batch, or still loading) we reserve the rail's footprint with
            an inert spacer so the picker card + empty/skeleton states keep the
            exact same width and horizontal position — no jump when a batch is
            chosen and the rail appears. */}
        {hasRoster ? (
          <SessionSummaryRail
            tally={tally}
            batchName={selectedBatch?.name ?? 'this batch'}
            editable={editable}
            busy={isBusy}
            submitting={submitMutation.isPending}
            dirty={canSubmit}
            unsaved={isDirty}
            unmarkedCount={tally.unmarked}
            submitted={isSubmitted}
            onAllPresent={() => setAll('present')}
            onAllAbsent={() => setAll('absent')}
            onSubmit={onSubmit}
          />
        ) : (
          <div aria-hidden="true" className="hidden w-[300px] flex-none min-[1120px]:block" />
        )}
      </div>

      {/* Frosted sticky bottom action bar (narrow viewports only). */}
      {hasRoster && editable ? (
        <div className="fixed inset-x-0 bottom-0 z-40 border-t border-[color:var(--color-border-strong)] bg-[color:var(--color-bg-elevated)]/90 px-4 pb-[calc(0.75rem+env(safe-area-inset-bottom))] pt-3 backdrop-blur-md min-[1120px]:hidden">
          <div className="mx-auto flex max-w-[940px] flex-col gap-2.5">
            <BulkButtons
              onAllPresent={() => setAll('present')}
              onAllAbsent={() => setAll('absent')}
              disabled={isBusy}
            />
            <SubmitButton
              onSubmit={onSubmit}
              submitting={submitMutation.isPending}
              dirty={canSubmit}
              unsaved={isDirty}
              unmarkedCount={tally.unmarked}
              submitted={isSubmitted}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
}

interface LockBannerProps {
  reason: LockReason;
}

/**
 * Read-only banner shown when the server reports the date is not editable for
 * this user. Past locks → admin-only message; future locks → future-date
 * message; a null reason means the role simply can't mark (e.g. a parent or
 * viewer on today) → a neutral permission message.
 */
function LockBanner({ reason }: LockBannerProps) {
  const message =
    reason === 'future'
      ? "You can't mark attendance for a future date."
      : reason === 'past'
        ? 'Only an admin can change attendance for past dates.'
        : "You don't have permission to mark attendance for this date.";
  return (
    <div
      role="status"
      className="mb-4 flex items-start gap-3 rounded-lg border border-[color:var(--color-border-strong)] bg-[color:var(--color-surface-strong)] px-4 py-3.5"
    >
      <span className="mt-0.5 flex-none text-[color:var(--color-text-muted)]" aria-hidden="true">
        {reason === 'future' ? <CalendarOff size={18} /> : <Lock size={18} />}
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-[color:var(--color-text)]">Locked</p>
        <p className="mt-0.5 text-sm text-[color:var(--color-text-muted)]">{message}</p>
      </div>
    </div>
  );
}

const LEGEND: ReadonlyArray<{ label: string; dot: string }> = [
  { label: 'Present', dot: 'bg-[color:var(--color-success)]' },
  { label: 'Late', dot: 'bg-[color:var(--color-warning)]' },
  { label: 'Absent', dot: 'bg-[color:var(--color-danger)]' },
  { label: 'Excused', dot: 'bg-gold-500' },
];

/**
 * Status legend. The "Not recorded" item appears only while some students are
 * still unmarked, so a fully-marked roster doesn't carry a stale grey key.
 */
function Legend({ showUnmarked }: { showUnmarked: boolean }) {
  const items = showUnmarked
    ? [
        ...LEGEND,
        { label: 'Not recorded', dot: 'bg-[color:var(--color-border-strong)]' },
      ]
    : LEGEND;
  return (
    <div className="flex flex-wrap gap-x-3.5 gap-y-1.5">
      {items.map((l) => (
        <span
          key={l.label}
          className="inline-flex items-center gap-1.5 text-[11.5px] text-[color:var(--color-text-muted)]"
        >
          <i aria-hidden="true" className={clsx('h-2 w-2 flex-none rounded-full', l.dot)} />
          {l.label}
        </span>
      ))}
    </div>
  );
}

function RosterSkeleton() {
  return (
    <div className="overflow-hidden rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] shadow-[var(--shadow-md)]">
      <div className="border-b border-[color:var(--color-border)] px-5 py-4">
        <Skeleton width="30%" height={16} />
        <div className="mt-2">
          <Skeleton width="45%" height={12} />
        </div>
      </div>
      {Array.from({ length: 6 }).map((_, i) => (
        <div
          key={i}
          className="flex items-center gap-3.5 border-b border-[color:var(--color-border)] px-5 py-3 last:border-b-0"
        >
          <Skeleton width={40} height={40} rounded="full" />
          <div className="flex-1 space-y-1.5">
            <Skeleton width="40%" height={13} />
            <Skeleton width="25%" height={11} />
          </div>
          <Skeleton width={140} height={30} rounded="full" />
        </div>
      ))}
    </div>
  );
}
